import React, { useState, useEffect } from 'react';
import { 
    Trophy, Calendar, ChevronRight, Loader2, 
    Brain, Info, CheckCircle2, AlertCircle,
    ArrowRight, Sparkles, History, Timer,
    ChevronLeft, User
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { pointService } from '../../../services/pointService';
import { gameService } from '../../../services/gameService';
import { getTeamName } from '../../../services/teamService';
import * as geminiService from '../../../services/geminiService';
import { fetchMatchDetails } from '../../../services/footballService';
import { TotoRound, TotoMatch, TotoPrediction } from '../../../types';
import { t, tr } from '../../../i18n';
import { TotoResultView } from './TotoResultView';
import { XlevenLogo } from '../../Common/XlevenLogo';
import { ConfirmModal } from '../../Common/ConfirmModal';
import { X } from 'lucide-react';
import { PointFeedback } from '../../Common/PointFeedback';
import SEO from '../../Common/SEO';

export const TotoGameView: React.FC = () => {
    const { lang } = useLanguage();
    const { user } = useAuth();
    const { showToast } = useToast();

    const [activeRound, setActiveRound] = useState<TotoRound | null>(null);
    const [matches, setMatches] = useState<TotoMatch[]>([]);
    const [predictions, setPredictions] = useState<Record<string, ('1' | 'X' | '2')[]>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isAlreadyParticipated, setIsAlreadyParticipated] = useState(false);
    const [participation, setParticipation] = useState<{ count: number; users: { avatar: string | null }[] }>({ count: 0, users: [] });
    const [isExpired, setIsExpired] = useState(false);
    
    // AI Analysis states
    const [analysisQueue, setAnalysisQueue] = useState<TotoMatch[]>([]);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [aiAnalysis, setAiAnalysis] = useState<Record<string, any>>({});
    const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
    const [isAnalysisAnimating, setIsAnalysisAnimating] = useState(false);
    const [selectedMatchForAI, setSelectedMatchForAI] = useState<TotoMatch | null>(null);
    const [isExitModalOpen, setIsExitModalOpen] = useState(false);
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [isInfoAnimating, setIsInfoAnimating] = useState(false);
    const [showResult, setShowResult] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    useEffect(() => {
        loadActiveRound();
    }, [user?.id]);

    useEffect(() => {
        if (activeRound) {
            const checkExpiry = () => {
                const now = new Date();
                const deadline = new Date(activeRound.end_at);
                setIsExpired(now > deadline);
            };
            
            checkExpiry();
            const timer = setInterval(checkExpiry, 10000); // Check every 10 seconds
            return () => clearInterval(timer);
        }
    }, [activeRound]);

    const loadActiveRound = async () => {
        setIsLoading(true);
        try {
            const rounds = await gameService.getTotoRounds();
            const openRound = rounds.find(r => r.status === 'open');
            if (openRound) {
                setActiveRound(openRound);
                const [matchData, participationData, existingPrediction] = await Promise.all([
                    gameService.getTotoMatches(openRound.id),
                    gameService.getTotoRoundParticipation(openRound.id),
                    user ? gameService.getUserTotoPrediction(openRound.id, user.id) : Promise.resolve(null)
                ]);
                
                setMatches(matchData);
                setParticipation(participationData);
                setIsAlreadyParticipated(!!existingPrediction);

                // Load existing analyses from LocalStorage first, then Supabase
                const localAnalysesStr = localStorage.getItem(`toto_analysis_${user?.id}`);
                const localAnalyses = localAnalysesStr ? JSON.parse(localAnalysesStr) : {};
                
                if (user) {
                    const analyses: Record<string, any> = { ...localAnalyses };
                    const analysisResults = await Promise.all(matchData.map(match => 
                        gameService.getTotoMatchAnalysis(match.match_id, user.id)
                    ));
                    
                    matchData.forEach((match, idx) => {
                        if (analysisResults[idx]) {
                            analyses[match.match_id] = analysisResults[idx];
                        }
                    });
                    setAiAnalysis(analyses);
                    
                    // Sync synced data back to localStorage
                    localStorage.setItem(`toto_analysis_${user.id}`, JSON.stringify(analyses));
                }
                
                // 플레이 클릭 시(페이지 진입 시) 선택 내역 초기화
                setPredictions({});
            }
        } catch (error) {
            console.error('Failed to load toto round:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePrediction = (matchId: string, choice: '1' | 'X' | '2') => {
        if (isExpired) {
            showToast('error', t(lang, 'toto_round_expired'));
            return;
        }
        if (isAlreadyParticipated) {
            showToast('error', t(lang, 'toto_already_participated'));
            return;
        }
        setPredictions(prev => {
            const current = prev[matchId] || [];
            const isSelected = current.includes(choice);
            
            const next = { ...prev };
            if (isSelected) {
                delete next[matchId];
            } else {
                next[matchId] = [choice];
            }
            return next;
        });
    };

    const handleSubmit = async () => {
        if (!activeRound) return;
        
        if (!user) {
            setIsLoginModalOpen(true);
            return;
        }

        if (isExpired) {
            showToast('error', t(lang, 'toto_submit_expired'));
            return;
        }
        
        if (Object.keys(predictions).length < matches.length) {
            showToast('error', t(lang, 'toto_predict_all'));
            return;
        }

        setIsSubmitting(true);
        try {
            const success = await gameService.saveTotoPrediction({
                user_id: user.id,
                round_id: activeRound.id,
                predictions
            });
            if (success) {
                showToast('success', t(lang, 'toto_saved_success'));
                setTimeout(() => {
                    if (window.navigateTo) window.navigateTo('/gamestore');
                }, 1500);
            }
        } catch (error) {
            showToast('error', t(lang, 'toto_save_failed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (analysisQueue.length > 0 && !isAnalyzing) {
            processNextAnalysis();
        }
    }, [analysisQueue, isAnalyzing]);

    const processNextAnalysis = async () => {
        const match = analysisQueue[0];
        if (!match || !user) return;

        setIsAnalyzing(true);
        try {
            // Check points before analysis (requires 10p)
            const userPoints = await pointService.getUserPoints(user.id);
            if (userPoints < 10) {
                showToast('error', tr(lang, 'toto_insufficient_points', { amount: 10 }));
                setAnalysisQueue(prev => prev.filter(m => m.match_id !== match.match_id));
                setIsAnalyzing(false);
                return;
            }

            // Deduct 10 points for AI analysis
            const deduction = await pointService.deductPoints(user.id, 10, 'Triple Bet AI Analysis');
            if (!deduction.success) {
                showToast('error', t(lang, 'failedToDeductPoints'));
                setAnalysisQueue(prev => prev.filter(m => m.match_id !== match.match_id));
                setIsAnalyzing(false);
                return;
            }

            // 1. 이미 해당 경기에 대한 리포트가 있는지 확인
            const { supabase } = await import('../../../services/authService');
            const { data: existingReports } = await supabase
                .from('match_predictions')
                .select('*')
                .eq('post_id', match.match_id)
                .order('created_at', { ascending: false })
                .limit(1);

            if (existingReports && existingReports.length > 0) {
                const existing = existingReports[0];
                
                // 2. 기존 데이터가 있다면 해당 내용을 복사하여 저장
                const predictionData = {
                    post_id: match.match_id,
                    user_id: user.id,
                    predicted_winner: existing.predicted_winner,
                    predicted_score: existing.predicted_score,
                    win_probability: existing.win_probability,
                    analysis_reason: existing.analysis_reason,
                    analysis_reason_ko: existing.analysis_reason_ko,
                    // analysis_reason_ja: existing.analysis_reason_ja,
                    // analysis_reason_es: existing.analysis_reason_es,
                    // analysis_reason_fr: existing.analysis_reason_fr,
                    home_team_id: String(match.home_team_id),
                    away_team_id: String(match.away_team_id),
                    league_id: String(match.league_id),
                    matchdate: match.match_time ? new Date(match.match_time).toISOString() : new Date().toISOString(),
                };

                await supabase.from('match_predictions').insert(predictionData);
                
                // 3. 6초 대기 후 노출
                await new Promise(resolve => setTimeout(resolve, 6000));
                
                const winnerTeamId = existing.predicted_winner === match.home_team_name ? match.home_team_id : 
                                   existing.predicted_winner === match.away_team_name ? match.away_team_id : undefined;

                const analysisData = {
                    ...existing,
                    team_id: winnerTeamId
                };

                await gameService.saveTotoMatchAnalysis(match.match_id, user.id, {
                    ...analysisData,
                    league_id: match.league_id,
                    matchdate: match.match_time
                }, match);
                
                setAiAnalysis(prev => {
                    const next = {
                        ...prev,
                        [match.match_id]: analysisData
                    };
                    localStorage.setItem(`toto_analysis_${user.id}`, JSON.stringify(next));
                    return next;
                });
                showToast('success', t(lang, 'toto_ai_completed'));
            } else {
                // 기존 리포트가 없을 경우 새로 생성
                const result = await geminiService.predictMatchResult(
                    match.home_team_name,
                    match.away_team_name
                );

                if (result) {
                    const winnerTeamId = result.predicted_winner === match.home_team_name ? match.home_team_id : 
                                       result.predicted_winner === match.away_team_name ? match.away_team_id : undefined;

                    const analysisData = {
                        ...result,
                        team_id: winnerTeamId
                    };

                    const predictionData = {
                        post_id: match.match_id,
                        user_id: user.id,
                        predicted_winner: result.predicted_winner,
                        predicted_score: result.predicted_score,
                        win_probability: result.win_probability,
                        analysis_reason: result.analysis_reason,
                        analysis_reason_ko: result.analysis_reason_ko,
                        // analysis_reason_ja: result.analysis_reason_ja,
                        // analysis_reason_es: result.analysis_reason_es,
                        // analysis_reason_fr: result.analysis_reason_fr,
                        home_team_id: String(match.home_team_id),
                        away_team_id: String(match.away_team_id),
                        league_id: String(match.league_id),
                        matchdate: match.match_time ? new Date(match.match_time).toISOString() : new Date().toISOString(),
                    };
                    
                    await supabase.from('match_predictions').insert(predictionData);

                    // DB에 저장 (유저별, 팀 ID, 리그 ID, 경기 날짜 포함)
                    await gameService.saveTotoMatchAnalysis(match.match_id, user.id, {
                        ...analysisData,
                        league_id: match.league_id,
                        matchdate: match.match_time
                    }, match);
                    
                    setAiAnalysis(prev => {
                        const next = {
                            ...prev,
                            [match.match_id]: analysisData
                        };
                        // 로컬스토리지 저장
                        localStorage.setItem(`toto_analysis_${user.id}`, JSON.stringify(next));
                        return next;
                    });
                    showToast('success', t(lang, 'toto_ai_completed'));
                }
            }
        } catch (error) {
            console.error('AI Analysis failed:', error);
            showToast('error', t(lang, 'toto_ai_error'));
        } finally {
            setAnalysisQueue(prev => prev.slice(1));
            setIsAnalyzing(false);
        }
    };

    const handleAIAnalysis = async (match: TotoMatch) => {
        if (aiAnalysis[match.match_id]) {
            setSelectedMatchForAI(match);
            setIsAnalysisModalOpen(true);
            setTimeout(() => setIsAnalysisAnimating(true), 10);
            return;
        }

        if (isExpired) {
            showToast('error', t(lang, 'toto_round_expired'));
            return;
        }

        if (!user) {
            showToast('error', t(lang, 'toto_login_required'));
            return;
        }

        if (analysisQueue.some(m => m.match_id === match.match_id)) {
            return;
        }

        setAnalysisQueue(prev => [...prev, match]);
    };

    const handleViewAnalysis = (match: TotoMatch) => {
        setSelectedMatchForAI(match);
        setIsAnalysisModalOpen(true);
        setTimeout(() => setIsAnalysisAnimating(true), 10);
    };

    const handleCloseAnalysis = () => {
        setIsAnalysisAnimating(false);
        setTimeout(() => {
            setIsAnalysisModalOpen(false);
            setSelectedMatchForAI(null);
        }, 500);
    };

    const handleOpenInfo = () => {
        setIsInfoModalOpen(true);
        setTimeout(() => setIsInfoAnimating(true), 10);
    };

    const handleCloseInfo = () => {
        setIsInfoAnimating(false);
        setTimeout(() => {
            setIsInfoModalOpen(false);
        }, 500);
    };

    const handleExit = () => {
        if (Object.keys(predictions).length > 0) {
            setIsExitModalOpen(true);
        } else {
            if (window.navigateTo) window.navigateTo('/gamestore');
        }
    };

    const handleLoginRedirect = () => {
        setIsLoginModalOpen(false);
        if (window.navigateTo) window.navigateTo('/login');
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-3">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{t(lang, 'loading')}</p>
            </div>
        );
    }

    if (showResult) {
        return <TotoResultView onBack={() => setShowResult(false)} />;
    }

    if (!activeRound) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
                <div className="w-20 h-20 bg-slate-50 rounded-[32px] flex items-center justify-center text-slate-200 mb-6">
                    <Trophy size={40} />
                </div>
                <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-2">
                    {t(lang, 'toto_no_round_title')}
                </h2>
                <p className="text-sm font-medium text-slate-400 max-w-[280px] mb-8">
                    {t(lang, 'toto_no_round_desc')}
                </p>
                <div className="flex flex-col gap-3 w-full max-w-[240px]">
                    <button 
                        onClick={() => setShowResult(true)}
                        className="flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-black transition-all active:scale-95 shadow-lg shadow-slate-200"
                    >
                        <History size={16} />
                        {t(lang, 'toto_history')}
                    </button>
                    <button 
                        onClick={() => {
                            if (window.navigateTo) window.navigateTo('/gamestore');
                        }}
                        className="flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-white border border-slate-200 text-slate-600 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-all active:scale-95 shadow-sm"
                    >
                        <ArrowRight size={16} />
                        {lang === 'ko' ? '게임 스토어로 이동' : 'Go to Game Store'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 pb-40">
            <SEO 
                title={t(lang, 'toto_seo_title')}
                description={t(lang, 'toto_seo_description')}
                keywords={t(lang, 'toto_seo_keywords')}
            />
            {/* Header */}
            <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-slate-100 px-4 py-3 md:px-6 md:py-4">
                <div className="max-w-2xl mx-auto space-y-3 md:space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 md:gap-3">
                            <button 
                                onClick={handleExit}
                                className="w-9 h-9 md:w-10 md:h-10 bg-slate-100 text-slate-600 rounded-xl md:rounded-2xl flex items-center justify-center hover:bg-slate-200 transition-all active:scale-95 shrink-0"
                            >
                                <ChevronLeft size={20} className="md:w-6 md:h-6" />
                            </button>
                            <div className="min-w-0">
                                <h1 className="text-sm md:text-lg font-black text-slate-900 leading-tight uppercase tracking-tight truncate">
                                    {activeRound[`title_${lang}` as keyof TotoRound] || activeRound.title_ko}
                                </h1>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <Timer size={10} className="text-slate-400 shrink-0" />
                                    <span className="text-[8px] md:text-[10px] font-bold text-slate-400 uppercase tracking-wider truncate">
                                        {lang === 'ko' ? '마감' : 'Ends'}: {new Date(activeRound.end_at).toLocaleDateString()} {new Date(activeRound.end_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-1.5 md:gap-2">
                            <button 
                                onClick={() => window.navigateTo && window.navigateTo('/gamestore/toto/result')}
                                className="p-2.5 md:px-4 md:py-3 bg-white border border-slate-200 text-slate-600 rounded-xl md:rounded-2xl text-[10px] md:text-[11px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all active:scale-95 shadow-sm flex items-center gap-2"
                                title={t(lang, 'toto_history')}
                            >
                                <History size={14} />
                                {/* <span className="hidden sm:inline">{t(lang, 'toto_history')}</span> */}
                            </button>
                            <button 
                                onClick={handleSubmit}
                                disabled={isSubmitting || Object.keys(predictions).length < 14 || isExpired}
                                className="px-4 py-2.5 md:px-6 md:py-3 bg-slate-900 text-white rounded-xl md:rounded-2xl text-[10px] md:text-[11px] font-black uppercase tracking-widest hover:bg-black transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-slate-200 flex items-center gap-2"
                            >
                                {isSubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 size={14} />}
                                <span className="whitespace-nowrap">{t(lang, 'toto_submit')}</span>
                            </button>
                        </div>
                    </div>

                    <div className="px-0.5">
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[8px] md:text-[9px] font-black text-slate-400 uppercase tracking-[0.15em]">{t(lang, 'toto_predictions_status')}</span>
                            <div className="flex items-center gap-2">
                                <span className="text-[9px] md:text-[10px] font-black text-blue-600 tabular-nums">
                                    {Object.keys(predictions).length} / 14
                                </span>
                            </div>
                        </div>
                        <div className="h-1 md:h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                                className="h-full bg-blue-600 rounded-full transition-all duration-700 ease-out"
                                style={{ width: `${Math.min((Object.keys(predictions).length / 14) * 100, 100)}%` }}
                            />
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
                {/* Info Card */}
                <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[32px] p-6 text-white shadow-xl shadow-blue-100 relative overflow-hidden group">
                    <button 
                        onClick={handleOpenInfo}
                        className="absolute top-4 right-4 z-20 w-8 h-8 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center transition-all active:scale-95"
                    >
                        <Info size={16} className="text-white" />
                    </button>
                    <div className="relative z-10">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-2 opacity-80">{t(lang, 'toto_challenge_win')}</p>
                        <h2 className="text-2xl font-black leading-tight mb-4">
                            {t(lang, 'toto_predict_title')}<br />{t(lang, 'toto_win_points')}
                        </h2>
                        <div className="flex items-center gap-4">
                            <div className="flex -space-x-3">
                                {participation.users.map((u, i) => (
                                    <div key={i} className="w-8 h-8 rounded-full border-2 border-blue-600 bg-slate-100 overflow-hidden">
                                        {u.avatar ? (
                                            <img src={u.avatar} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-blue-100 text-blue-600">
                                                <User size={14} />
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {participation.users.length === 0 && (
                                    <div className="w-8 h-8 rounded-full border-2 border-blue-600 bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                        <Trophy size={14} className="text-white" />
                                    </div>
                                )}
                            </div>
                            <span className="text-xs font-bold opacity-90">
                                {tr(lang, 'toto_players_joined', { count: participation.count.toLocaleString() })}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Match List */}
                <div className="space-y-3">
                    {matches.map((match, idx) => (
                        <div key={match.id} className="bg-white rounded-[28px] p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md uppercase tracking-widest">
                                    Game {idx + 1}
                                </span>
                                <div className="flex items-center gap-2">
                                    {aiAnalysis[match.match_id] ? (
                                        <button 
                                            onClick={() => handleViewAnalysis(match)}
                                            className="flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-indigo-100 transition-colors"
                                        >
                                            <Brain size={12} />
                                            {t(lang, 'toto_view_analysis')}
                                        </button>
                                    ) : (
                                        <button 
                                            onClick={() => handleAIAnalysis(match)}
                                            disabled={analysisQueue.some(m => m.match_id === match.match_id) || isExpired}
                                            className="flex items-center gap-1.5 px-3 py-1 bg-slate-50 text-slate-500 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-blue-50 hover:text-blue-600 transition-colors disabled:opacity-50"
                                        >
                                            {analysisQueue.some(m => m.match_id === match.match_id) ? (
                                                <Loader2 size={12} className="animate-spin" />
                                            ) : (
                                                <Brain size={12} />
                                            )}
                                            {analysisQueue.some(m => m.match_id === match.match_id) ? (lang === 'ko' ? '분석 중...' : 'Analyzing...') : t(lang, 'toto_ai_analysis')}
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center justify-between gap-4 mb-6 relative">
                                <div className="flex flex-col items-center gap-2 flex-1 text-center">
                                    <img src={match.home_team_logo} alt="" className="w-12 h-12 object-contain" />
                                    <span className="text-xs font-black text-slate-900 truncate w-full">
                                        {getTeamName(match.home_team_id, lang, match.home_team_name)}
                                    </span>
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                    {match.status === 'FT' || match.status === 'Finished' ? (
                                        <div className="flex flex-col items-center">
                                            <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded uppercase tracking-widest mb-1">
                                                {lang === 'ko' ? '종료' : 'FT'}
                                            </span>
                                            <div className="flex items-center gap-2 px-3 py-1 bg-slate-900 text-white rounded-full">
                                                <span className={`text-xs font-black ${match.result === '1' ? 'text-yellow-400' : ''}`}>
                                                    {match.result === '1' ? 'W' : match.result === 'X' ? 'D' : 'L'}
                                                </span>
                                                <div className="w-px h-2 bg-white/20" />
                                                <span className={`text-xs font-black ${match.result === '2' ? 'text-yellow-400' : ''}`}>
                                                    {match.result === '2' ? 'W' : match.result === 'X' ? 'D' : 'L'}
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <span className="text-[10px] font-black text-slate-300 uppercase tracking-tighter">VS</span>
                                            <span className="text-[10px] font-bold text-slate-400 whitespace-nowrap">{match.match_time.split(' ')[1]}</span>
                                        </>
                                    )}
                                </div>
                                <div className="flex flex-col items-center gap-2 flex-1 text-center">
                                    <img src={match.away_team_logo} alt="" className="w-12 h-12 object-contain" />
                                    <span className="text-xs font-black text-slate-900 truncate w-full">
                                        {getTeamName(match.away_team_id, lang, match.away_team_name)}
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { id: '1', label: t(lang, 'toto_win') },
                                    { id: 'X', label: t(lang, 'toto_draw') },
                                    { id: '2', label: t(lang, 'toto_loss') }
                                ].map(choice => {
                                    const isResult = match.result === choice.id;
                                    const isSelected = predictions[match.match_id]?.includes(choice.id as any);
                                    
                                    return (
                                        <button
                                            key={choice.id}
                                            onClick={() => handlePrediction(match.match_id, choice.id as any)}
                                            className={`py-3.5 rounded-2xl text-xs font-black transition-all active:scale-95 relative overflow-hidden ${
                                                isSelected
                                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 ring-2 ring-blue-600 ring-offset-2'
                                                : isResult
                                                ? 'bg-emerald-50 text-emerald-600 border-2 border-emerald-500/20'
                                                : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                                            }`}
                                        >
                                            {choice.label}
                                            {isResult && (
                                                <div className="absolute top-1 right-1">
                                                    <CheckCircle2 size={10} className="text-emerald-500" />
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </main>

            {/* Bottom Action Spacer */}
            <div className="h-20" />

            {/* Exit Confirmation Modal */}
            <ConfirmModal
                isOpen={isExitModalOpen}
                onClose={() => setIsExitModalOpen(false)}
                onConfirm={() => {
                    setIsExitModalOpen(false);
                    if (window.navigateTo) window.navigateTo('/gamestore');
                }}
                title={t(lang, 'toto_exit_title')}
                description={t(lang, 'toto_exit_desc')}
                confirmLabel={t(lang, 'toto_exit_confirm')}
                cancelLabel={t(lang, 'toto_exit_cancel')}
            />

            {/* Info Modal */}
            {isInfoModalOpen && (
                <div className={`fixed inset-0 z-[500] flex flex-col justify-end sm:items-center sm:justify-center transition-opacity duration-500 ${isInfoAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={handleCloseInfo}></div>
                    <div className={`relative bg-white w-full sm:max-w-lg rounded-t-[40px] sm:rounded-[40px] shadow-2xl overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isInfoAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-8 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
                                    <Trophy size={20} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">
                                        {t(lang, 'toto_rules_title')}
                                    </h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t(lang, 'toto_rules_policy')}</p>
                                </div>
                            </div>
                            <button onClick={handleCloseInfo} className="p-2 text-slate-300 hover:text-slate-900 transition-colors">
                                <X size={24} />
                            </button>
                        </header>
                        
                        <div className="px-6 pb-10 space-y-6">
                            <div className="space-y-2 text-sm font-medium text-slate-600 leading-relaxed">
                                <p>• {t(lang, 'toto_rules_1')}</p>
                                <p>• {t(lang, 'toto_rules_2')}</p>
                                <p>• {t(lang, 'toto_rules_3')}</p>
                                <p>• {t(lang, 'toto_rules_4')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* AI Analysis Modal */}
            {isAnalysisModalOpen && selectedMatchForAI && (
                <div className={`fixed inset-0 z-[500] flex flex-col justify-end sm:items-center sm:justify-center transition-opacity duration-500 ${isAnalysisAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={handleCloseAnalysis}></div>
                    <div className={`relative bg-white w-full sm:max-w-lg rounded-t-[40px] sm:rounded-[40px] shadow-2xl overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isAnalysisAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-8 border-b border-slate-50 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600">
                                    <Brain size={20} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">
                                        {t(lang, 'toto_ai_analysis')}
                                    </h3>
                                </div>
                            </div>
                            <button onClick={handleCloseAnalysis} className="p-2 text-slate-300 hover:text-slate-900 transition-colors">
                                <X size={24} />
                            </button>
                        </header>
                        
                        <div className="p-8 max-h-[70vh] overflow-y-auto">
                            {analysisQueue.some(m => m.match_id === selectedMatchForAI.match_id) ? (
                                <div className="py-12 flex flex-col items-center justify-center gap-4">
                                    <div className="relative">
                                        <div className="w-16 h-16 border-4 border-indigo-100 rounded-full" />
                                        <div className="w-16 h-16 border-4 border-t-indigo-600 rounded-full animate-spin absolute inset-0" />
                                        <Brain className="absolute inset-0 m-auto text-indigo-600 animate-pulse" size={24} />
                                    </div>
                                    <p className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] animate-pulse">Analyzing match data...</p>
                                </div>
                            ) : aiAnalysis[selectedMatchForAI.match_id] ? (
                                <div className="space-y-6">
                                    <div className="flex items-center justify-around p-6 bg-slate-50 rounded-[32px]">
                                        <div className="text-center">
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{t(lang, 'toto_ai_winner')}</p>
                                            <p className="text-sm font-black text-slate-900">
                                                {(() => {
                                                    const predictedWinner = aiAnalysis[selectedMatchForAI.match_id].predicted_winner;
                                                    const winnerTeamId = predictedWinner === selectedMatchForAI.home_team_name ? selectedMatchForAI.home_team_id :
                                                                       predictedWinner === selectedMatchForAI.away_team_name ? selectedMatchForAI.away_team_id : null;
                                                    return winnerTeamId ? getTeamName(winnerTeamId, lang, predictedWinner) : predictedWinner;
                                                })()}
                                            </p>
                                        </div>
                                        <div className="h-8 w-px bg-slate-200" />
                                        <div className="text-center">
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{t(lang, 'toto_ai_score')}</p>
                                            <p className="text-lg font-black text-blue-600">{aiAnalysis[selectedMatchForAI.match_id].predicted_score}</p>
                                        </div>
                                        <div className="h-8 w-px bg-slate-200" />
                                        <div className="text-center">
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{t(lang, 'toto_ai_win_rate')}</p>
                                            <p className="text-sm font-black text-slate-900">{aiAnalysis[selectedMatchForAI.match_id].win_probability}%</p>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                            <Info size={14} /> {t(lang, 'toto_ai_tactical')}
                                        </h4>
                                        <div className="text-sm font-medium text-slate-600 leading-relaxed bg-indigo-50/50 p-6 rounded-[32px] whitespace-pre-line">
                                            {aiAnalysis[selectedMatchForAI.match_id][`analysis_reason_${lang === 'en' ? '' : lang}`.replace('_$', '')] || 
                                             aiAnalysis[selectedMatchForAI.match_id][`analysis_reason_${lang}`] || 
                                             aiAnalysis[selectedMatchForAI.match_id].analysis_reason_ko ||
                                             aiAnalysis[selectedMatchForAI.match_id].analysis_reason}
                                        </div>
                                    </div>

                                    <button 
                                        onClick={handleCloseAnalysis}
                                        className="w-full py-4 bg-slate-900 text-white rounded-[24px] text-xs font-black uppercase tracking-widest hover:bg-black transition-all"
                                    >
                                        {lang === 'ko' ? '확인했습니다' : 'Got it'}
                                    </button>
                                </div>
                            ) : (
                                <div className="py-12 text-center">
                                    <AlertCircle size={32} className="mx-auto text-slate-200 mb-2" />
                                    <p className="text-sm font-bold text-slate-400">
                                        {lang === 'ko' ? '분석 데이터를 불러오지 못했습니다.' : 'Failed to load analysis data.'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={isLoginModalOpen}
                onClose={() => setIsLoginModalOpen(false)}
                onConfirm={handleLoginRedirect}
                title={t(lang, 'loginRequired')}
                description={t(lang, 'loginParticipate')}
                confirmLabel={t(lang, 'signInLabel')}
                cancelLabel={t(lang, 'cancel')}
            />
        </div>
    );
};
